import { inject, Injectable } from "@angular/core";
import { LotApiService } from "./lot-api.service";
import { LotState, LotStoreService } from "./lot-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Lot, LotMouvement } from "../../models/lot.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { QuickInventairePayload } from "../../models/quick-inventaire.model";

@Injectable({ providedIn: "root" })
export class LotFacadeService extends EntityFacadeService<
  Lot,
  LotApiService,
  LotState,
  EntityMetadata,
  LotStoreService
> {
  constructor() {
    const api = inject(LotApiService);
    const store = inject(LotStoreService);
    super(api, store);
    this.store.initStatus(LotFacadeService);
  }

  @Untrack
  async loadByIdLight(id: string) {
    const lot = await firstValueFrom(this.api.loadOneLight(id));
    if (lot) this.store.upsertOne(lot);
    return lot;
  }

  @UpdateStatus
  async loadForArticle(articleId: string) {
    try {
      const lots = await firstValueFrom(this.api.loadForArticle(articleId));
      this.store.addMany(lots);
      return lots;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async loadUniqueForArticle(articleId: string) {
    try {
      const lot = await firstValueFrom(
        this.api.loadUniqueForArticle(articleId),
      );
      this.store.upsertOne(lot);
      return lot;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async loadLightForArticle(articleId: string) {
    try {
      const lots = await firstValueFrom(
        this.api.loadLightForArticle(articleId),
      );
      this.store.addMany(lots);
      return lots;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async loadLightStockForArticle(articleId: string) {
    try {
      const lots = await firstValueFrom(
        this.api.loadLightStockForArticle(articleId),
      );
      this.store.addMany(lots);
      return lots;
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async createLot(lotMouvement: LotMouvement) {
    try {
      const lot = await firstValueFrom(this.api.createLot(lotMouvement));
      this.store.upsertOne(lot);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async updateDocumentListeId(lotId: string, documentListeId: string) {
    try {
      const lot = await firstValueFrom(
        this.api.updateDocumentListeId(lotId, documentListeId),
      );
      this.store.upsertOne(lot);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @UpdateStatus
  async fusionnerLots(articleId: string) {
    try {
      const lot = await firstValueFrom(this.api.fusionnerLots(articleId));
      await this.loadForArticle(articleId);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async getAudit(lotId: string) {
    return await firstValueFrom(this.api.getAudit(lotId));
  }

  @Untrack
  @UpdateStatus
  async makeAMove(lotMouvement: LotMouvement) {
    return await firstValueFrom(this.api.makeAMove(lotMouvement));
  }

  @Untrack
  @UpdateStatus
  async quickInventaire(payload: QuickInventairePayload) {
    return await firstValueFrom(this.api.quickInventaire(payload));
  }

}
